import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangleIcon } from "../icons";
import Toast from "../ui/Toast";
import Spinner from "../ui/Spinner";
import { formatCurrency } from "../../utils/helpers";
import { useReportSettings } from "../../hooks/useReportSettings";
import { apiService } from "../../services/apiService";
import { canMutateTenantData } from "../../config/constants";

import CustomSelect from "../ui/CustomSelect";
const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function newId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function emptyMonths() {
  const o = {};
  for (let m = 1; m <= 12; m++) o[String(m)] = "";
  return o;
}

function parseNum(v) {
  if (v === "" || v == null) return 0;
  const n = parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function amountTotal(granularity, row) {
  if (granularity === "annual") return parseNum(row.annual);
  let s = 0;
  const mo = row.months || {};
  for (let m = 1; m <= 12; m++) s += parseNum(mo[String(m)] ?? mo[m]);
  return s;
}

/** Spread an annual amount across 12 months (cents-aware remainder to early months). */
function spreadAnnualToMonths(annual) {
  const totalCents = Math.round(parseNum(annual) * 100);
  const base = Math.floor(totalCents / 12);
  const rem = totalCents - base * 12;
  const months = {};
  for (let m = 1; m <= 12; m++) {
    const cents = base + (m <= rem ? 1 : 0);
    months[String(m)] = cents / 100;
  }
  return months;
}

function monthsFromRow(row, granularity) {
  if (granularity === "monthly") {
    const mo = { ...emptyMonths(), ...(row.months || {}) };
    for (let m = 1; m <= 12; m++) {
      const key = String(m);
      mo[key] = mo[key] === "" || mo[key] == null ? "" : String(mo[key]);
    }
    return mo;
  }
  return spreadAnnualToMonths(row.annual);
}

function ToggleGroup({ value, options, onChange, disabled }) {
  return (
    <div className="inline-flex h-9 rounded-md overflow-hidden border border-gray-300 bg-white">
      {options.map((opt, idx) => (
        <button
          key={opt.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          className={`px-4 text-sm font-medium transition-all disabled:opacity-60 ${
            idx > 0 ? "border-l border-gray-200" : ""
          } ${
            value === opt.value
            ? "bg-gray-100 text-gray-900 shadow-sm"
            : "text-gray-600 hover:bg-gray-50"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// function StepBadge({ n }) {
//   return (
//     <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-red text-[11px] font-semibold text-white">
//       {n}
//     </span>
//   );
// }

function StepChevron() {
  return (
    <div className="hidden lg:flex items-center justify-center px-2 text-gray-300" aria-hidden>
      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  );
}

function SegmentedChoice({ value, options, onChange, disabled }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 gap-0.5">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium transition-all disabled:opacity-60 ${
              active
              ? "bg-gray-100 text-gray-900 shadow-sm border border-gray-200"
              : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const IconCalendar = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const IconUsers = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const IconChart = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21.21 15.89A10 10 0 118 2.83" />
    <path d="M22 12A10 10 0 0012 2v10z" />
  </svg>
);

const IconInfo = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </svg>
);

function MoneyInput({ value, onChange, disabled, className = "" }) {
  return (
    <input
      type="text"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`w-full max-w-[160px] ml-auto border border-gray-300 rounded py-1.5 px-2 text-right text-sm disabled:bg-gray-50 ${className}`}
    />
  );
}

function formatMoneyDisplay(n) {
  return formatCurrency(parseNum(n));
}

// Small, self-contained confirm-delete modal styled to match the app's
// existing "Confirm Delete" dialog (white card, bold header, gray warning
// box, Cancel + red destructive action).
function ConfirmDeleteModal({ label, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-delete-title"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h3 id="confirm-delete-title" className="text-base font-semibold text-gray-900">
          Confirm Delete
        </h3>

        <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
          This will remove <span className="font-semibold">{label}</span> from this budget.
        </div>

        <p className="mt-3 text-xs text-gray-500">
          This action cannot be undone.
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

const BudgetEntryView = ({
  onBack,
  providers = [],
  glCodes = [],
  refreshDropdownValues,
  userRole,
  tenantId,
  embedded = false,
  canEdit: canEditProp,
  onFooterAction,
}) => {
  const {
    providerBudgetByYear,
    loading,
    error,
    saveProviderBudget,
  } = useReportSettings(tenantId);

  const canEdit =
    canEditProp != null ? Boolean(canEditProp) : canMutateTenantData(userRole);

  const yearOptions = useMemo(() => {
    const existingYears = Object.keys(providerBudgetByYear || {}).map(Number);
    const currentYear = new Date().getFullYear();
    const minYear = Math.min(...existingYears, currentYear - 5);
    const maxYear = Math.max(...existingYears, currentYear + 8);
    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  }, [providerBudgetByYear]);

  const [viewTab, setViewTab] = useState("enter");
  const [budgetYear, setBudgetYear] = useState(() => new Date().getFullYear());
  const [entryMode, setEntryMode] = useState("annual");
  const [trackBy, setTrackBy] = useState("provider");
  const [activeMonth, setActiveMonth] = useState(1);
  const [providerRows, setProviderRows] = useState([]);
  const [wholeRows, setWholeRows] = useState([]);
  const [closedMonths, setClosedMonths] = useState([]);
  const [savingClosedMonths, setSavingClosedMonths] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ message: null, type: "success" });

  // Pending delete confirmation. `kind` is one of "provider" | "providerGl" | "wholeGl".
  const [pendingDelete, setPendingDelete] = useState(null);

  const yearlyData = providerBudgetByYear?.[String(budgetYear)] || {
    granularity: null,
    entryType: null,
    lines: [],
    annual_budget: 0,
  };

  const serverGranularity = yearlyData.granularity || null;
  const serverEntryType = yearlyData.entryType || null;
  const isModeLocked = Boolean(serverGranularity);
  const isTypeLocked = Boolean(serverEntryType);
  const effectiveGranularity = serverGranularity || entryMode;
  const effectiveTrackBy = serverEntryType || trackBy;

  const providerOptions = useMemo(
    () =>
      [...new Set(providers || [])]
        .filter(Boolean)
        .sort((a, b) =>
          String(a).localeCompare(String(b), undefined, { numeric: true }),
        ),
    [providers],
  );

  const tenantGlCodes = useMemo(
    () => (glCodes || []).filter((g) => g && g.gl_code),
    [glCodes],
  );

  const glByCode = useMemo(() => {
    const map = new Map();
    tenantGlCodes.forEach((g) => {
      map.set(String(g.gl_code).toLowerCase(), g);
    });
    return map;
  }, [tenantGlCodes]);

  const serverPayloadKey = useMemo(
    () =>
      JSON.stringify({
        g: yearlyData?.granularity ?? null,
        t: yearlyData?.entryType ?? null,
        lines: yearlyData?.lines ?? [],
      }),
    [providerBudgetByYear, budgetYear],
  );

  useEffect(() => {
    const fetchClosedMonths = async () => {
      try {
        const params = new URLSearchParams({ year: String(budgetYear) });
        if (tenantId != null && tenantId !== "") {
          params.set("tenant_id", String(tenantId));
        }
        const res = await apiService.request(
          `/analytics/closed-months?${params.toString()}`,
        );
        setClosedMonths(res.closed_months || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchClosedMonths();
  }, [budgetYear, tenantId]);

  const hydrateGlLine = useCallback((row, shape) => {
    const glCode = row.gl_code || row.glCode || "";
    const meta = glByCode.get(String(glCode).toLowerCase());
    const base = {
      id: row.id || newId(),
      glCode,
      description:
        row.description ||
        meta?.description ||
        "",
    };
    if (shape === "monthly") {
      return { ...base, months: { ...emptyMonths(), ...(row.months || {}) } };
    }
    return {
      ...base,
      annual: row.annual != null && row.annual !== "" ? String(row.annual) : "",
    };
  }, [glByCode]);

  useEffect(() => {
    if (loading) return;
    setPageError(null);

    const y = providerBudgetByYear?.[String(budgetYear)];
    const fromServer = Array.isArray(y?.lines) ? y.lines : [];
    const g = y?.granularity || null;
    const t = y?.entryType || null;
    const shape = g || "annual";

    if (g) setEntryMode(g);
    if (t) setTrackBy(t);

    if (!fromServer.length) {
      setProviderRows([]);
      setWholeRows([]);
      return;
    }

    const looksWhole =
      t === "whole" ||
      (!t && fromServer.every((row) => row.gl_code || row.glCode) && !fromServer.some((row) => row.provider));

    if (looksWhole) {
      setTrackBy(t || "whole");
      setWholeRows(fromServer.map((row) => hydrateGlLine(row, shape)));
      setProviderRows([]);
      return;
    }

    setTrackBy(t || "provider");
    setWholeRows([]);
    setProviderRows(
      fromServer
        .filter((row) => row.provider)
        .map((row) => {
          const rawGl = Array.isArray(row.gl_lines)
            ? row.gl_lines
            : Array.isArray(row.glLines)
              ? row.glLines
              : null;
          let glLines;
          if (rawGl && rawGl.length) {
            glLines = rawGl.map((g) => hydrateGlLine(g, shape));
          } else if (shape === "annual" && (row.annual != null || row.months)) {
            // Legacy provider-only row → one blank GL so the amount is not lost.
            glLines = [
              hydrateGlLine(
                {
                  id: newId(),
                  gl_code: "",
                  description: "",
                  annual: row.annual,
                  months: row.months,
                },
                shape,
              ),
            ];
          } else if (shape === "monthly" && row.months) {
            glLines = [
              hydrateGlLine(
                {
                  id: newId(),
                  gl_code: "",
                  description: "",
                  months: row.months,
                },
                shape,
              ),
            ];
          } else {
            glLines = [];
          }
          return {
            id: row.id || newId(),
            provider: row.provider || "",
            expanded: true,
            glLines,
          };
        }),
    );
  }, [loading, budgetYear, serverPayloadKey, hydrateGlLine, providerBudgetByYear]);

  const requestModeChange = (mode) => {
    if (isModeLocked || mode === entryMode) return;
    const hasData =
      providerRows.some((p) => p.glLines?.length) || wholeRows.length > 0;
    if (hasData && !window.confirm("Changing mode clears amounts. Continue?")) {
      return;
    }
    setEntryMode(mode);
    if (mode === "annual") {
      setProviderRows((prev) =>
        prev.map((p) => ({
          ...p,
          glLines: (p.glLines || []).map((g) => ({
            id: g.id,
            glCode: g.glCode,
            description: g.description,
            annual: "",
          })),
        })),
      );
      setWholeRows((prev) =>
        prev.map((g) => ({
          id: g.id,
          glCode: g.glCode,
          description: g.description,
          annual: "",
        })),
      );
    } else {
      setProviderRows((prev) =>
        prev.map((p) => ({
          ...p,
          glLines: (p.glLines || []).map((g) => ({
            id: g.id,
            glCode: g.glCode,
            description: g.description,
            months: emptyMonths(),
          })),
        })),
      );
      setWholeRows((prev) =>
        prev.map((g) => ({
          id: g.id,
          glCode: g.glCode,
          description: g.description,
          months: emptyMonths(),
        })),
      );
    }
  };

  const requestTrackChange = (next) => {
    if (isTypeLocked || next === trackBy) return;
    const hasData =
      providerRows.some((p) => p.provider || p.glLines?.length) ||
      wholeRows.length > 0;
    if (
      hasData &&
      !window.confirm("Changing how you track budget clears the current draft. Continue?")
    ) {
      return;
    }
    setTrackBy(next);
    setProviderRows([]);
    setWholeRows([]);
  };

  const providerTotal = useCallback(
    (row) =>
      (row.glLines || []).reduce(
        (s, g) => s + amountTotal(effectiveGranularity, g),
        0,
      ),
    [effectiveGranularity],
  );

  const allProvidersTotal = useMemo(
    () => providerRows.reduce((s, p) => s + providerTotal(p), 0),
    [providerRows, providerTotal],
  );

  const wholeTotal = useMemo(
    () => wholeRows.reduce((s, g) => s + amountTotal(effectiveGranularity, g), 0),
    [wholeRows, effectiveGranularity],
  );

  const monthProvidersTotal = useMemo(() => {
    const key = String(activeMonth);
    return providerRows.reduce(
      (sum, p) =>
        sum +
        (p.glLines || []).reduce(
          (s, g) => s + parseNum(g.months?.[key]),
          0,
        ),
      0,
    );
  }, [providerRows, activeMonth]);

  const monthWholeTotal = useMemo(() => {
    const key = String(activeMonth);
    return wholeRows.reduce((s, g) => s + parseNum(g.months?.[key]), 0);
  }, [wholeRows, activeMonth]);

  const overviewRows = useMemo(() => {
    const byGl = new Map();
    const addGl = (gl) => {
      const code = (gl.glCode || "").trim();
      if (!code) return;
      const months = monthsFromRow(gl, effectiveGranularity);
      const existing = byGl.get(code) || {
        glCode: code,
        description: gl.description || "",
        months: emptyMonths(),
      };
      if (!existing.description && gl.description) {
        existing.description = gl.description;
      }
      for (let m = 1; m <= 12; m++) {
        const key = String(m);
        existing.months[key] =
          parseNum(existing.months[key]) + parseNum(months[key]);
      }
      byGl.set(code, existing);
    };

    if (effectiveTrackBy === "whole") {
      wholeRows.forEach(addGl);
    } else {
      providerRows.forEach((p) => (p.glLines || []).forEach(addGl));
    }

    return [...byGl.values()].sort((a, b) =>
      a.glCode.localeCompare(b.glCode, undefined, { numeric: true }),
    );
  }, [effectiveTrackBy, providerRows, wholeRows, effectiveGranularity]);

  const overviewAnnualTotal = useMemo(
    () =>
      overviewRows.reduce((sum, row) => {
        let s = 0;
        for (let m = 1; m <= 12; m++) s += parseNum(row.months[String(m)]);
        return sum + s;
      }, 0),
    [overviewRows],
  );

  const addProvider = () => {
    setProviderRows((prev) => [
      ...prev,
      {
        id: newId(),
        provider: "",
        expanded: true,
        glLines: [
          effectiveGranularity === "monthly"
            ? { id: newId(), glCode: "", description: "", months: emptyMonths() }
            : { id: newId(), glCode: "", description: "", annual: "" },
        ],
      },
    ]);
  };

  const addGlToProvider = (providerId) => {
    setProviderRows((prev) =>
      prev.map((p) =>
        p.id !== providerId
          ? p
          : {
              ...p,
              expanded: true,
              glLines: [
                ...(p.glLines || []),
                effectiveGranularity === "monthly"
                  ? { id: newId(), glCode: "", description: "", months: emptyMonths() }
                  : { id: newId(), glCode: "", description: "", annual: "" },
              ],
            },
      ),
    );
  };

  const addWholeGl = () => {
    setWholeRows((prev) => [
      ...prev,
      effectiveGranularity === "monthly"
        ? { id: newId(), glCode: "", description: "", months: emptyMonths() }
        : { id: newId(), glCode: "", description: "", annual: "" },
    ]);
  };

  const updateProvider = (id, patch) => {
    setProviderRows((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    );
  };

  const updateProviderGl = (providerId, glId, patch) => {
    setProviderRows((prev) =>
      prev.map((p) => {
        if (p.id !== providerId) return p;
        return {
          ...p,
          glLines: (p.glLines || []).map((g) => {
            if (g.id !== glId) return g;
            const next = { ...g, ...patch };
            if (patch.glCode != null) {
              const meta = glByCode.get(String(patch.glCode).toLowerCase());
              next.description = meta?.description || "";
            }
            return next;
          }),
        };
      }),
    );
  };

  const updateWholeGl = (glId, patch) => {
    setWholeRows((prev) =>
      prev.map((g) => {
        if (g.id !== glId) return g;
        const next = { ...g, ...patch };
        if (patch.glCode != null) {
          const meta = glByCode.get(String(patch.glCode).toLowerCase());
          next.description = meta?.description || "";
        }
        return next;
      }),
    );
  };

  const removeProvider = (id) => {
    setProviderRows((prev) => prev.filter((p) => p.id !== id));
  };

  const removeProviderGl = (providerId, glId) => {
    setProviderRows((prev) =>
      prev.map((p) =>
        p.id !== providerId
          ? p
          : { ...p, glLines: (p.glLines || []).filter((g) => g.id !== glId) },
      ),
    );
  };

  const removeWholeGl = (glId) => {
    setWholeRows((prev) => prev.filter((g) => g.id !== glId));
  };

  // --- Delete confirmation orchestration ---------------------------------
  // Each "Delete" button now opens the shared modal instead of deleting
  // immediately. `pendingDelete` carries enough context to both render a
  // useful label and perform the right removal once confirmed.

  const requestDeleteProvider = (provider) => {
    setPendingDelete({
      kind: "provider",
      providerId: provider.id,
      label: provider.provider?.trim() ? provider.provider.trim() : "this provider",
    });
  };

  const requestDeleteProviderGl = (providerId, gl) => {
    setPendingDelete({
      kind: "providerGl",
      providerId,
      glId: gl.id,
      label: gl.glCode?.trim()
        ? `GL code ${gl.glCode.trim()}`
        : "this GL code",
    });
  };

  const requestDeleteWholeGl = (gl) => {
    setPendingDelete({
      kind: "wholeGl",
      glId: gl.id,
      label: gl.glCode?.trim()
        ? `GL code ${gl.glCode.trim()}`
        : "this GL code",
    });
  };

  const cancelPendingDelete = () => setPendingDelete(null);

  const confirmPendingDelete = () => {
    if (!pendingDelete) return;
    if (pendingDelete.kind === "provider") {
      removeProvider(pendingDelete.providerId);
    } else if (pendingDelete.kind === "providerGl") {
      removeProviderGl(pendingDelete.providerId, pendingDelete.glId);
    } else if (pendingDelete.kind === "wholeGl") {
      removeWholeGl(pendingDelete.glId);
    }
    setPendingDelete(null);
  };

  const buildGlPayload = (g) => {
    if (effectiveGranularity === "annual") {
      return {
        id: g.id,
        gl_code: g.glCode.trim(),
        description: g.description || "",
        annual: parseNum(g.annual),
      };
    }
    const months = {};
    for (let m = 1; m <= 12; m++) {
      months[String(m)] = parseNum(g.months?.[String(m)]);
    }
    return {
      id: g.id,
      gl_code: g.glCode.trim(),
      description: g.description || "",
      months,
    };
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    setPageError(null);

    try {
      let payloadLines = [];

      if (effectiveTrackBy === "whole") {
        const filled = wholeRows.filter((g) => g.glCode?.trim());
        if (!filled.length) {
          setPageError("Add at least one GL code");
          return;
        }
        const seenGl = new Set();
        for (const g of filled) {
          const key = g.glCode.trim().toLowerCase();
          if (seenGl.has(key)) {
            setPageError(`GL code ${g.glCode.trim()} is already used`);
            return;
          }
          seenGl.add(key);
        }
        payloadLines = filled.map(buildGlPayload);
      } else {
        const filledProviders = providerRows.filter((p) => p.provider?.trim());
        if (!filledProviders.length) {
          setPageError("Add at least one provider");
          return;
        }
        const seenProviders = new Set();
        for (const p of filledProviders) {
          const provKey = p.provider.trim().toLowerCase();
          if (seenProviders.has(provKey)) {
            setPageError(`Provider ${p.provider.trim()} is already used`);
            return;
          }
          seenProviders.add(provKey);

          const gls = (p.glLines || []).filter((g) => g.glCode?.trim());
          if (!gls.length) {
            setPageError(`Add a GL code under ${p.provider}`);
            return;
          }
          const seenGl = new Set();
          for (const g of gls) {
            const key = g.glCode.trim().toLowerCase();
            if (seenGl.has(key)) {
              setPageError(
                `GL code ${g.glCode.trim()} is already used under ${p.provider.trim()}`,
              );
              return;
            }
            seenGl.add(key);
          }
        }
        payloadLines = filledProviders.map((p) => ({
          id: p.id,
          provider: p.provider.trim(),
          gl_lines: (p.glLines || [])
            .filter((g) => g.glCode?.trim())
            .map(buildGlPayload),
        }));
      }

      const result = await saveProviderBudget({
        year: budgetYear,
        granularity: serverGranularity || entryMode,
        entryType: serverEntryType || trackBy,
        lines: payloadLines,
      });

      if (!result.success) {
        setPageError(result.error || "Save failed");
        return;
      }

      setToast({ message: "Budget saved successfully", type: "success" });
      if (refreshDropdownValues) await refreshDropdownValues();
    } catch (e) {
      setPageError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveClosedMonths = async () => {
    setSavingClosedMonths(true);
    try {
      const body = {
        year: budgetYear,
        closed_months: closedMonths,
      };
      if (tenantId != null && tenantId !== "") {
        body.tenant_id = tenantId;
      }
      const res = await apiService.request(`/analytics/closed-months`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      const queued = Array.isArray(res?.summary_queued) ? res.summary_queued : [];
      setToast({
        message:
          queued.length > 0
            ? `Closed months saved successfully. Monthly note generating for ${queued.length} month(s).`
            : "Closed months saved successfully",
        type: "success",
      });
    } catch (err) {
      setPageError(err.message);
    } finally {
      setSavingClosedMonths(false);
    }
  };

  // Let a host (e.g. the tenant settings modal) render the row actions in its
  // own footer. The refs keep the exposed handler identities stable.
  const footerHandlersRef = useRef(null);
  footerHandlersRef.current = {
    budget: handleSave,
    closedMonths: saveClosedMonths,
    add: effectiveTrackBy === "whole" ? addWholeGl : addProvider,
  };
  const triggerSaveBudget = useCallback(
    () => footerHandlersRef.current.budget(),
    [],
  );
  const triggerSaveClosedMonths = useCallback(
    () => footerHandlersRef.current.closedMonths(),
    [],
  );
  const triggerAdd = useCallback(() => footerHandlersRef.current.add(), []);
  const hostRendersSaveAction = typeof onFooterAction === "function";

  useEffect(() => {
    if (!hostRendersSaveAction) return undefined;
    let action = null;
    if (canEdit && viewTab === "enter") {
      action = {
        label: "Save budget",
        save: triggerSaveBudget,
        saving,
        addLabel:
          effectiveTrackBy === "whole" ? "+ Add GL code" : "+ Add provider",
        add: triggerAdd,
      };
    } else if (canEdit && viewTab === "closed-months") {
      action = {
        label: "Save closed months",
        save: triggerSaveClosedMonths,
        saving: savingClosedMonths,
      };
    }
    onFooterAction(action);
    return () => onFooterAction(null);
  }, [
    hostRendersSaveAction,
    onFooterAction,
    viewTab,
    canEdit,
    saving,
    savingClosedMonths,
    effectiveTrackBy,
    triggerSaveBudget,
    triggerSaveClosedMonths,
    triggerAdd,
  ]);

  const toggleMonth = (m) => {
    setClosedMonths((prev) =>
      prev.includes(m)
        ? prev.filter((x) => x !== m)
        : [...prev, m].sort((a, b) => a - b),
    );
  };

  const downloadCsv = () => {
    const headers = [
      "GL CODE",
      "DESCRIPTION",
      ...MONTH_LABELS.map((l) => l.toUpperCase()),
      "ANNUAL",
    ];
    const lines = overviewRows.map((row) => {
      const months = [];
      let annual = 0;
      for (let m = 1; m <= 12; m++) {
        const v = parseNum(row.months[String(m)]);
        annual += v;
        months.push(String(v));
      }
      return [
        row.glCode,
        row.description || "",
        ...months,
        String(annual),
      ]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(",");
    });
    const totalMonths = [];
    let totalAnnual = 0;
    for (let m = 1; m <= 12; m++) {
      const v = overviewRows.reduce(
        (s, row) => s + parseNum(row.months[String(m)]),
        0,
      );
      totalMonths.push(String(v));
      totalAnnual += v;
    }
    lines.push(
      ["Total budget", "", ...totalMonths, String(totalAnnual)]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(","),
    );
    const blob = new Blob([[headers.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `budget-overview-${budgetYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const title =
  viewTab === "overview"
    ? "Budget overview"
    : viewTab === "closed-months"
      ? "Closed Months"
      : effectiveTrackBy === "whole"
        ? "Whole budget"
        : "Provider budgets";

  const subtitle =
    viewTab === "overview"
      ? "Read-only view of the full budget by month and GL code."
      : viewTab === "closed-months"
        ? "Read-only view of the closed months. Only closed months are included in YTD and forecast calculations."
      : effectiveTrackBy === "whole"
        ? effectiveGranularity === "monthly"
          ? "The whole budget in one place, no provider split. Enter each GL code's amount for the month; the twelve months tally into the annual."
          : "The whole budget in one place, no provider split. Enter each GL code as an annual amount; it spreads evenly across the twelve months."
        : effectiveGranularity === "monthly"
          ? "Enter monthly amounts per GL code under each provider. Amounts here tally into an annual budget."
          : "Enter each GL code as an annual amount. It spreads evenly across the twelve months, and the lines add up to the provider and to the whole budget.";

  if (loading) {
    return (
      <div className={embedded ? "py-12 flex justify-center" : "pt-[155px] flex justify-center"}>
        <Spinner />
      </div>
    );
  }

  const amountLabel =
    effectiveGranularity === "annual"
      ? "ANNUAL AMOUNT"
      : `${MONTH_NAMES[activeMonth - 1].toUpperCase()} AMOUNT`;

  const renderGlTable = ({
    rows,
    onChangeGl,
    onChangeAmount,
    onDelete,
    onAdd,
    showAdd,
    readOnlyStructure,
  }) => (
    <div className="border border-gray-200 rounded-md overflow-hidden bg-white">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide text-gray-600">
            <th className="text-left py-2 px-3 font-semibold">GL code</th>
            <th className="text-left py-2 px-3 font-semibold">Description</th>
            <th className="text-right py-2 px-3 font-semibold">{amountLabel}</th>
            {canEdit && <th className="w-16" />}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={canEdit ? 4 : 3} className="py-6 px-3 text-center text-gray-500">
                No GL codes yet.
              </td>
            </tr>
          )}
          {rows.map((g) => {
            const usedByOthers = new Set(
              (rows || [])
                .filter((row) => row.id !== g.id && String(row.glCode || "").trim())
                .map((row) => String(row.glCode).trim().toLowerCase()),
            );
            return (
            <tr key={g.id} className="border-b border-gray-100">
              <td className="py-2 px-3">
                {readOnlyStructure || !canEdit ? (
                  <span className="text-gray-800">
                    {g.glCode}
                    {g.description ? ` · ${g.description}` : ""}
                  </span>
                ) : (
                  <CustomSelect
                    value={g.glCode}
                    onChange={(e) => onChangeGl(g.id, e.target.value)}
                    className="w-full border border-gray-300 rounded py-1.5 px-2 text-sm bg-white"
                  >
                    <option value="">Select GL code…</option>
                    {tenantGlCodes.map((opt) => {
                      const code = String(opt.gl_code || "").trim();
                      const inUse = usedByOthers.has(code.toLowerCase());
                      const label = `${code}${
                        opt.description ? ` · ${opt.description}` : ""
                      }${inUse ? " (in use)" : ""}`;
                      return (
                        <option
                          key={opt.gl_code}
                          value={opt.gl_code}
                          disabled={inUse}
                        >
                          {label}
                        </option>
                      );
                    })}
                  </CustomSelect>
                )}
              </td>
              <td className="py-2 px-3">
                <input
                  type="text"
                  value={g.description || ""}
                  readOnly
                  className="w-full border border-gray-200 rounded py-1.5 px-2 text-sm bg-gray-50 text-gray-700"
                />
              </td>
              <td className="py-2 px-3 text-right">
                <MoneyInput
                  value={
                    effectiveGranularity === "annual"
                      ? g.annual ?? ""
                      : g.months?.[String(activeMonth)] ?? ""
                  }
                  disabled={!canEdit}
                  onChange={(v) => onChangeAmount(g.id, v)}
                />
              </td>
              {canEdit && (
                <td className="py-2 px-2">
                  {!readOnlyStructure && (
                    <button
                      type="button"
                      onClick={() => onDelete(g)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  )}
                </td>
              )}
            </tr>
            );
          })}
        </tbody>
      </table>
      {canEdit && showAdd && !readOnlyStructure && (
        <div className="px-3 py-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onAdd}
            className="h-8 px-3 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-50"
          >
            + Add GL code
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={embedded ? "bg-transparent" : "min-h-screen pt-[155px] bg-brand-background"}>
      <div className="fixed top-4 right-4 z-[110]">
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: null, type: "success" })}
        />
      </div>

      <div className={embedded ? "w-full" : "max-w-full lg:max-w-[85%] mx-5 lg:mx-auto mt-6"}>
        <div className={embedded ? "bg-transparent mb-2" : "bg-white rounded-md shadow-sm border-0 overflow-hidden mb-6"}>
          {!embedded && (
            <div className="border-b border-gray-200 px-6 py-4 flex items-center gap-4">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  ← Back
                </button>
              )}
              <h2 className="text-lg font-semibold text-gray-900">Budget Entry</h2>
            </div>
          )}

          <div className={embedded ? "space-y-6 pb-2" : "p-6 space-y-6"}>
            {(pageError || error) && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                <AlertTriangleIcon className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">
                  {pageError?.message || pageError || error?.message || String(error)}
                </p>
              </div>
            )}

            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-[240px]">
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                <p className="text-xs text-gray-600 mt-1 max-w-2xl">{subtitle}</p>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2">
                <ToggleGroup
                  value={viewTab}
                  onChange={setViewTab}
                  options={[
                    { value: "enter", label: "Enter" },
                    { value: "overview", label: "Overview" },
                    { value: "closed-months", label: "Closed Months" },
                  ]}
                />
                {viewTab === "overview" && (
                  <>
                    <CustomSelect
                      value={budgetYear}
                      onChange={(e) => setBudgetYear(parseInt(e.target.value, 10))}
                      className="h-9 bg-white border border-gray-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </CustomSelect>
                    <button
                      type="button"
                      onClick={downloadCsv}
                      className="h-9 px-3 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-50"
                    >
                      Export 
                    </button>
                  </>
                )}

{viewTab === "closed-months" && (
                  <>
                    <CustomSelect
                      value={budgetYear}
                      onChange={(e) => setBudgetYear(parseInt(e.target.value, 10))}
                      className="h-9 bg-white border border-gray-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </CustomSelect>
                  </>
                )}
              </div>
            </div>

            {viewTab === "enter" && (
              <>
                <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-4 lg:gap-2 p-4 lg:p-5 items-start">
                    {/* Step 1 */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {/* <StepBadge n={1} /> */}
                        <h4 className="text-sm font-semibold text-gray-900">Select Year</h4>
                      </div>
                      <p className="text-xs text-gray-500 mb-3 ml-8">
                        Choose the year for this budget.
                      </p>
                      <div className="ml-0 sm:ml-8 relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                          <IconCalendar />
                        </span>
                        <CustomSelect
                          value={budgetYear}
                          onChange={(e) => setBudgetYear(parseInt(e.target.value, 10))}
                          className="h-10 w-full appearance-none bg-white border border-gray-300 rounded-lg pl-9 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
                        >
                          {yearOptions.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </CustomSelect>
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                          ▾
                        </span>
                      </div>
                    </div>

                    <StepChevron />

                    {/* Step 2 */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {/* <StepBadge n={2} /> */}
                        <h4 className="text-sm font-semibold text-gray-900">Budget Type</h4>
                      </div>
                      <p className="text-xs text-gray-500 mb-3 ml-8">
                        Choose how you want to plan.
                      </p>
                      <div className="ml-0 sm:ml-8">
                        <SegmentedChoice
                          value={effectiveGranularity}
                          disabled={!canEdit || isModeLocked}
                          onChange={requestModeChange}
                          options={[
                            {
                              value: "annual",
                              label: "Annual",
                              icon: <IconCalendar className="h-3.5 w-3.5" />,
                            },
                            {
                              value: "monthly",
                              label: "Monthly",
                              icon: <IconCalendar className="h-3.5 w-3.5" />,
                            },
                          ]}
                        />
                        {isModeLocked && (
                          <p className="text-xs text-gray-500 mt-1.5">
                            locked for {budgetYear} ({serverGranularity})
                          </p>
                        )}
                      </div>
                    </div>

                    <StepChevron />

                    {/* Step 3 */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {/* <StepBadge n={3} /> */}
                        <h4 className="text-sm font-semibold text-gray-900">Track By</h4>
                      </div>
                      <p className="text-xs text-gray-500 mb-3 ml-8">
                        Choose the tracking method.
                      </p>
                      <div className="ml-0 sm:ml-8">
                        <SegmentedChoice
                          value={effectiveTrackBy}
                          disabled={!canEdit || isTypeLocked}
                          onChange={requestTrackChange}
                          options={[
                            {
                              value: "whole",
                              label: "Whole budget",
                              icon: <IconChart className="h-3.5 w-3.5" />,
                            },
                            {
                              value: "provider",
                              label: "Provider",
                              icon: <IconUsers className="h-3.5 w-3.5" />,
                            },
                          ]}
                        />
                        {isTypeLocked && (
                          <p className="text-xs text-gray-500 mt-1.5">
                            locked for {budgetYear} ({serverEntryType})
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 px-4 py-3 bg-gray-50">
                    <span className="mt-0.5 text-primary-90 shrink-0">
                      <IconInfo className="h-4 w-4" />
                    </span>
                    <p className="text-sm text-gray-700">
                      You are creating an{" "}
                      <span className="font-semibold text-gray-800">
                        {effectiveGranularity === "annual" ? "Annual" : "Monthly"}
                      </span>{" "}
                      budget for the year{" "}
                      <span className="font-semibold text-gray-800">{budgetYear}</span>
                      , tracking by{" "}
                      <span className="font-semibold text-gray-800">
                        {effectiveTrackBy === "whole" ? "Whole budget" : "Provider"}
                      </span>
                      .
                    </p>
                  </div>
                </div>

                {effectiveGranularity === "monthly" && (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-50 bg-gray-50 px-4 py-3">
                    <p className="text-sm text-gray-800">
                      Entering the <strong className="text-gray-900">{MONTH_NAMES[activeMonth - 1]}</strong> column.
                      Amounts here tally into the annual budget.
                    </p>
                    <CustomSelect
                      value={activeMonth}
                      onChange={(e) => setActiveMonth(parseInt(e.target.value, 10))}
                      className="h-9 bg-white border border-gray-300 rounded-md px-3 text-sm"
                    >
                      {MONTH_NAMES.map((name, idx) => (
                        <option key={name} value={idx + 1}>
                          {name}
                        </option>
                      ))}
                    </CustomSelect>
                  </div>
                )}

                {effectiveTrackBy === "provider" ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-xs uppercase tracking-wide text-gray-500 px-1">
                      <span>Provider</span>
                      <span>
                        {effectiveGranularity === "annual"
                          ? "Annual budget"
                          : `${MONTH_NAMES[activeMonth - 1]} budget`}
                      </span>
                    </div>

                    {providerRows.length === 0 && (
                      <div className="border border-dashed border-gray-300 rounded-md py-10 text-center text-sm text-gray-500">
                        No providers yet. Add a provider, choose GL codes, enter amounts, then save.
                      </div>
                    )}

                    {providerRows.map((p) => {
                      const total =
                        effectiveGranularity === "annual"
                          ? providerTotal(p)
                          : (p.glLines || []).reduce(
                              (s, g) =>
                                s + parseNum(g.months?.[String(activeMonth)]),
                              0,
                            );
                      const glCount = (p.glLines || []).filter((g) => g.glCode).length;
                      return (
                        <div
                          key={p.id}
                          className="border border-gray-200 rounded-md overflow-hidden"
                        >
                          <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-gray-50">
                            <button
                              type="button"
                              onClick={() =>
                                updateProvider(p.id, { expanded: !p.expanded })
                              }
                              className="text-gray-500 hover:text-gray-800 w-6"
                              aria-label={p.expanded ? "Collapse" : "Expand"}
                            >
                              {p.expanded ? "▾" : "▸"}
                            </button>
                            <CustomSelect
                              value={p.provider}
                              disabled={!canEdit}
                              onChange={(e) =>
                                updateProvider(p.id, { provider: e.target.value })
                              }
                              className="min-w-[180px] border border-gray-300 rounded py-1.5 px-2 text-sm bg-white"
                            >
                              <option value="">Select provider…</option>
                              {providerOptions.map((name) => {
                                const inUse = providerRows.some(
                                  (other) =>
                                    other.id !== p.id &&
                                    String(other.provider || "")
                                      .trim()
                                      .toLowerCase() ===
                                      String(name).trim().toLowerCase(),
                                );
                                return (
                                  <option
                                    key={name}
                                    value={name}
                                    disabled={inUse}
                                  >
                                    {inUse ? `${name} (in use)` : name}
                                  </option>
                                );
                              })}
                            </CustomSelect>
                            <span className="inline-flex items-center rounded-full bg-slate-200 text-slate-700 text-xs px-2.5 py-0.5">
                              {glCount === 1
                                ? `GL ${(p.glLines || []).find((g) => g.glCode)?.glCode || ""}`
                                : `${glCount || 0} GL codes`}
                            </span>
                            <div className="ml-auto flex items-center gap-3">
                              <span className="text-sm font-semibold text-gray-900">
                                {formatMoneyDisplay(total)}
                              </span>
                              {canEdit && (
                                <button
                                  type="button"
                                  onClick={() => requestDeleteProvider(p)}
                                  className="text-xs text-red-600 hover:text-red-800"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>

                          {p.expanded && (
                            <div className="p-3 space-y-2">
                              {renderGlTable({
                                rows: p.glLines || [],
                                onChangeGl: (glId, code) =>
                                  updateProviderGl(p.id, glId, { glCode: code }),
                                onChangeAmount: (glId, v) => {
                                  if (effectiveGranularity === "annual") {
                                    updateProviderGl(p.id, glId, { annual: v });
                                  } else {
                                    updateProviderGl(p.id, glId, {
                                      months: {
                                        ...(p.glLines.find((g) => g.id === glId)?.months ||
                                          emptyMonths()),
                                        [String(activeMonth)]: v,
                                      },
                                    });
                                  }
                                },
                                onDelete: (gl) => requestDeleteProviderGl(p.id, gl),
                                onAdd: () => addGlToProvider(p.id),
                                showAdd: true,
                                readOnlyStructure: false,
                              })}
                              <div className="text-right text-sm text-gray-700 px-1">
                                {effectiveGranularity === "annual"
                                  ? "Provider total"
                                  : `${MONTH_NAMES[activeMonth - 1]} total`}{" "}
                                <strong>{formatMoneyDisplay(total)}</strong>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {canEdit && !hostRendersSaveAction && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={addProvider}
                          className="h-9 px-3 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-50"
                        >
                          + Add provider
                        </button>
                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={saving}
                          className="px-4 py-2 rounded-md text-sm font-medium bg-white border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                        >
                          {saving ? "Saving..." : "Save budget"}
                        </button>
                      </div>
                    )}

                    <div className="flex items-center justify-between rounded-md bg-[var(--accent,#a51c1c)] text-white px-4 py-3 shadow-lg">
                      <span className="text-sm font-medium">All providers total</span>
                           
                      <span className="text-sm font-semibold">
                        {formatMoneyDisplay(
                          effectiveGranularity === "annual"
                            ? allProvidersTotal
                            : monthProvidersTotal,
                        )}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {renderGlTable({
                      rows: wholeRows,
                      onChangeGl: (glId, code) =>
                        updateWholeGl(glId, { glCode: code }),
                      onChangeAmount: (glId, v) => {
                        if (effectiveGranularity === "annual") {
                          updateWholeGl(glId, { annual: v });
                        } else {
                          const row = wholeRows.find((g) => g.id === glId);
                          updateWholeGl(glId, {
                            months: {
                              ...(row?.months || emptyMonths()),
                              [String(activeMonth)]: v,
                            },
                          });
                        }
                      },
                      onDelete: requestDeleteWholeGl,
                      onAdd: addWholeGl,
                      showAdd: false,
                      readOnlyStructure: false,
                    })}

                    {effectiveGranularity === "monthly" && wholeRows.length > 0 && (
                      <p className="text-xs text-gray-500">
                        Monthly amounts for each GL code tally into the annual total.
                      </p>
                    )}

                    {canEdit && !hostRendersSaveAction && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={addWholeGl}
                          className="h-9 px-3 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-50"
                        >
                          + Add GL code
                        </button>
                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={saving}
                          className="px-4 py-2 rounded-md text-sm font-medium bg-white border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                        >
                          {saving ? "Saving..." : "Save budget"}
                        </button>
                      </div>
                    )}

                    <div className="flex items-center justify-between rounded-md bg-[var(--accent,#a51c1c)] text-white px-4 py-3 shadow-lg">
                      <span className="text-sm font-medium">Whole budget total</span>
                      <span className="text-sm font-semibold">
                        {formatMoneyDisplay(
                          effectiveGranularity === "annual"
                            ? wholeTotal
                            : monthWholeTotal,
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}

            {viewTab === "overview" && (
              <div className="space-y-4">
                <p className="text-sm text-gray-700">
                  {budgetYear} budget · {formatMoneyDisplay(overviewAnnualTotal)}
                  {" — "}
                  {effectiveTrackBy === "whole"
                    ? "one combined budget"
                    : "by provider"}
                </p>

                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-600">
                          <th className="text-left py-2 px-3 font-semibold sticky left-0 bg-gray-50">
                            GL code
                          </th>
                          {MONTH_LABELS.map((label) => (
                            <th
                              key={label}
                              className="text-right py-2 px-2 font-semibold whitespace-nowrap"
                            >
                              {label}
                            </th>
                          ))}
                          <th className="text-right py-2 px-3 font-semibold border-l border-gray-200">
                            Annual
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {overviewRows.length === 0 && (
                          <tr>
                            <td
                              colSpan={14}
                              className="py-10 text-center text-gray-500"
                            >
                              No budget data for {budgetYear}. Use Enter to add amounts.
                            </td>
                          </tr>
                        )}
                        {overviewRows.map((row) => {
                          let annual = 0;
                          return (
                            <tr key={row.glCode} className="border-b border-gray-100">
                              <td className="py-2 px-3 sticky left-0 bg-white whitespace-nowrap">
                                <span className="font-medium text-gray-900">
                                  {row.glCode}
                                </span>
                                {row.description ? (
                                  <span className="text-gray-500"> {row.description}</span>
                                ) : null}
                              </td>
                              {MONTH_LABELS.map((_, idx) => {
                                const v = parseNum(row.months[String(idx + 1)]);
                                annual += v;
                                return (
                                  <td
                                    key={idx}
                                    className="py-2 px-2 text-right text-gray-800 whitespace-nowrap"
                                  >
                                    {v.toLocaleString("en-US", {
                                      maximumFractionDigits: 2,
                                    })}
                                  </td>
                                );
                              })}
                              <td className="py-2 px-3 text-right font-medium border-l border-gray-100 whitespace-nowrap">
                                {annual.toLocaleString("en-US", {
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {overviewRows.length > 0 && (
                        <tfoot>
                          <tr className="bg-[var(--accent,#a51c1c)] text-white font-semibold">
                            <td className="py-2 px-3 sticky left-0 bg-[var(--accent,#a51c1c)]">
                              Total budget
                            </td>
                            {MONTH_LABELS.map((_, idx) => {
                              const v = overviewRows.reduce(
                                (s, row) =>
                                  s + parseNum(row.months[String(idx + 1)]),
                                0,
                              );
                              return (
                                <td
                                  key={idx}
                                  className="py-2 px-2 text-right whitespace-nowrap"
                                >
                                  {v.toLocaleString("en-US", {
                                    maximumFractionDigits: 2,
                                  })}
                                </td>
                              );
                            })}
                            <td className="py-2 px-3 text-right border-l border-primary-600 whitespace-nowrap">
                              {overviewAnnualTotal.toLocaleString("en-US", {
                                maximumFractionDigits: 2,
                              })}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Closed months — unchanged reporting control */}
{viewTab === "closed-months" && (
  <div className="space-y-4">
    <div className="rounded-md border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-medium text-gray-800">
            Reporting months
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Select the months that are closed for reporting.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
        {MONTH_LABELS.map((label, idx) => {
          const m = idx + 1;
          const active = closedMonths.includes(m);

          return (
            <button
              key={m}
              type="button"
              onClick={() => toggleMonth(m)}
              disabled={!canEdit}
              className={`px-2 py-2 text-sm rounded-md border transition ${
                active
                  ? "bg-red-500 text-white border-red-500"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>

    {canEdit && !hostRendersSaveAction && (
      <div className="flex justify-end">
        <button
          type="button"
          onClick={saveClosedMonths}
          disabled={savingClosedMonths}
          className="px-4 py-2 rounded-md text-sm font-medium bg-white border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
        >
          {savingClosedMonths ? "Saving..." : "Save closed months"}
        </button>
      </div>
    )}
  </div>
)}
          </div>
        </div>
      </div>

      {pendingDelete && (
        <ConfirmDeleteModal
          label={pendingDelete.label}
          onCancel={cancelPendingDelete}
          onConfirm={confirmPendingDelete}
        />
      )}
    </div>
  );
};

export default BudgetEntryView;
